/**
 * Super-Admin · Scraper Admin Console
 *
 * Tabs:
 *   1. Overview   — live stats, health status
 *   2. Config     — enable/disable, batch size, API credentials
 *   3. Activity   — paginated recent sync log
 */
import { useCallback, useEffect, useState } from "react";
import {
    Activity, CheckCircle2, XCircle, RefreshCw, Copy, Eye, EyeOff,
    Settings as Cog, ClipboardList, Zap, Loader2, AlertTriangle,
    Database, Clock, Hash, ToggleLeft, ToggleRight, Save, RotateCcw,
    ShieldCheck, ShieldAlert, KeyRound,
} from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { PageHeader } from "@/components/Ui";
import { fmtDateTime } from "@/lib/time";

const TABS = [
    { id: "overview", label: "Overview",  icon: Activity },
    { id: "config",   label: "Config",    icon: Cog },
    { id: "activity", label: "Activity",  icon: ClipboardList },
];

export default function AdminScraper() {
    const [tab, setTab] = useState("overview");
    return (
        <>
            <PageHeader
                title="Scraper API"
                subtitle="Manage the platform-level CNR scraper sync. Configure credentials, batch limits, and monitor ingestion activity."
            />
            <div className="flex items-center gap-1 border-b border-border mb-6 overflow-x-auto">
                {TABS.map(t => {
                    const Icon = t.icon;
                    return (
                        <button
                            key={t.id}
                            data-testid={`tab-scraper-${t.id}`}
                            onClick={() => setTab(t.id)}
                            className={`flex items-center gap-2 px-4 py-3 text-xs uppercase tracking-[0.16em] whitespace-nowrap transition-colors ${
                                tab === t.id
                                    ? "text-primary border-b-2 border-primary"
                                    : "text-muted-foreground hover:text-primary"
                            }`}
                        >
                            <Icon className="w-3.5 h-3.5" strokeWidth={1.5} />
                            {t.label}
                        </button>
                    );
                })}
            </div>
            {tab === "overview" && <OverviewTab />}
            {tab === "config"   && <ConfigTab />}
            {tab === "activity" && <ActivityTab />}
        </>
    );
}

// ── Overview ─────────────────────────────────────────────────────────────────

function OverviewTab() {
    const [stats, setStats]   = useState(null);
    const [loading, setLoading] = useState(true);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/admin/scraper/stats");
            setStats(data);
        } catch (e) {
            toast.error(apiError(e));
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    if (loading) return <LoadingSpinner />;

    const cards = [
        { label: "Total CNRs in DB",   value: stats?.total_cnrs   ?? "—", icon: Database, color: "text-blue-500" },
        { label: "Scraper-synced",      value: stats?.total_synced ?? "—", icon: Zap,      color: "text-violet-500" },
        { label: "Synced today",        value: stats?.synced_today ?? "—", icon: Hash,     color: "text-emerald-500" },
        { label: "Max batch size",      value: stats?.max_batch_size ?? "—", icon: ClipboardList, color: "text-amber-500" },
    ];

    return (
        <div className="space-y-6">
            {/* Status banner */}
            <div className={`flex items-center gap-3 px-4 py-3 rounded-lg border text-sm font-medium ${
                stats?.endpoint_enabled
                    ? "bg-emerald-500/10 border-emerald-500/30 text-emerald-600 dark:text-emerald-400"
                    : "bg-red-500/10 border-red-500/30 text-red-600 dark:text-red-400"
            }`}>
                {stats?.endpoint_enabled
                    ? <><CheckCircle2 className="w-4 h-4" /> Scraper endpoint is <strong>enabled</strong> and accepting pushes</>
                    : <><XCircle className="w-4 h-4" /> Scraper endpoint is <strong>disabled</strong> — no syncs accepted</>
                }
                <button
                    data-testid="btn-scraper-refresh-stats"
                    onClick={load}
                    className="ml-auto p-1 rounded hover:bg-black/10 dark:hover:bg-white/10"
                    title="Refresh"
                >
                    <RefreshCw className="w-3.5 h-3.5" />
                </button>
            </div>

            {/* Stat cards */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                {cards.map(c => {
                    const Icon = c.icon;
                    return (
                        <div key={c.label} className="rounded-xl border border-border bg-card p-4 space-y-2">
                            <div className="flex items-center gap-2 text-muted-foreground text-xs uppercase tracking-wide">
                                <Icon className={`w-3.5 h-3.5 ${c.color}`} strokeWidth={1.5} />
                                {c.label}
                            </div>
                            <div className="text-2xl font-semibold tabular-nums">{c.value}</div>
                        </div>
                    );
                })}
            </div>

            {/* Last sync info */}
            {stats?.last_sync_at && (
                <div className="rounded-xl border border-border bg-card p-4 flex items-start gap-3">
                    <Clock className="w-4 h-4 text-muted-foreground mt-0.5 shrink-0" />
                    <div className="text-sm space-y-0.5">
                        <div className="font-medium">Last scraped case</div>
                        <div className="text-muted-foreground font-mono text-xs">{stats.last_sync_cnr}</div>
                        <div className="text-muted-foreground text-xs">{fmtDateTime(stats.last_sync_at)}</div>
                    </div>
                </div>
            )}

            {/* Endpoint reference */}
            <div className="rounded-xl border border-border bg-card p-4 space-y-3">
                <div className="text-xs uppercase tracking-widest text-muted-foreground font-medium">Endpoint Reference</div>
                <div className="space-y-3 font-mono text-xs">
                    {[
                        ["GET",  "/api/health",           "Public health check"],
                        ["GET",  "/api/v1/sync/cnrs",     "Paginated CNR list (Bearer)"],
                        ["POST", "/api/v1/sync/case",     "Push single case (Bearer + HMAC)"],
                    ].map(([method, path, desc]) => (
                        <div key={path} className="flex flex-col gap-0.5">
                            <div className="flex items-center gap-2">
                                <span className={`shrink-0 px-1.5 py-0.5 rounded text-[10px] font-bold ${
                                    method === "GET" ? "bg-blue-500/15 text-blue-600 dark:text-blue-400"
                                                     : "bg-amber-500/15 text-amber-600 dark:text-amber-400"
                                }`}>{method}</span>
                                <span className="text-foreground break-all">{path}</span>
                            </div>
                            <span className="text-muted-foreground pl-10">{desc}</span>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
}

// ── Config ───────────────────────────────────────────────────────────────────

function ConfigTab() {
    const [cfg, setCfg]         = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving]   = useState(false);
    const [form, setForm]       = useState({});
    const [showKey, setShowKey]       = useState(false);
    const [showSecret, setShowSecret] = useState(false);
    const [rotatingKey, setRotatingKey]       = useState(false);
    const [rotatingSecret, setRotatingSecret] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/admin/scraper/config");
            setCfg(data);
            setForm({
                enabled:        data.enabled,
                max_batch_size: data.max_batch_size,
                description:    data.description ?? "",
            });
        } catch (e) {
            toast.error(apiError(e));
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    const save = async () => {
        setSaving(true);
        try {
            const { data } = await api.patch("/admin/scraper/config", {
                enabled:        form.enabled,
                max_batch_size: parseInt(form.max_batch_size, 10),
                description:    form.description,
            });
            setCfg(data);
            toast.success("Scraper config saved");
        } catch (e) {
            toast.error(apiError(e));
        } finally {
            setSaving(false);
        }
    };

    const copyToClipboard = (val, label) => {
        if (!val) return;
        navigator.clipboard.writeText(val).then(() => toast.success(`${label} copied`));
    };

    const saveCustomKey = async (val) => {
        const { data } = await api.post("/admin/scraper/credentials", { sync_api_key: val });
        setCfg(data);
        toast.success("SYNC_API_KEY saved");
    };

    const saveCustomSecret = async (val) => {
        const { data } = await api.post("/admin/scraper/credentials", { sync_hmac_secret: val });
        setCfg(data);
        toast.success("SYNC_HMAC_SECRET saved");
    };

    const rotateKey = async () => {
        try {
            const { data } = await api.post("/admin/scraper/credentials", { rotate_key: true });
            setCfg(data);
            toast.success("SYNC_API_KEY generated and saved");
        } catch (e) { toast.error(apiError(e)); }
        finally { setRotatingKey(false); }
    };

    const rotateSecret = async () => {
        setRotatingSecret(true);
        try {
            const { data } = await api.post("/admin/scraper/credentials", { rotate_secret: true });
            setCfg(data);
            toast.success("SYNC_HMAC_SECRET generated and saved");
        } catch (e) { toast.error(apiError(e)); }
        finally { setRotatingSecret(false); }
    };

    const generateBoth = async () => {
        setRotatingKey(true);
        setRotatingSecret(true);
        try {
            const { data } = await api.post("/admin/scraper/credentials", { rotate_key: true, rotate_secret: true });
            setCfg(data);
            toast.success("Both credentials generated and saved");
        } catch (e) { toast.error(apiError(e)); }
        finally { setRotatingKey(false); setRotatingSecret(false); }
    };

    if (loading) return <LoadingSpinner />;

    const keyMissing    = !cfg?.api_key_set;
    const secretMissing = !cfg?.hmac_secret_set;
    const anyMissing    = keyMissing || secretMissing;

    return (
        <div className="max-w-2xl space-y-6">
            {/* Credential alert banner */}
            {anyMissing && (
                <div className="flex items-start gap-3 rounded-xl border border-amber-500/40 bg-amber-500/8 p-4"
                    data-testid="cred-missing-banner">
                    <ShieldAlert className="w-5 h-5 text-amber-500 shrink-0 mt-0.5" />
                    <div>
                        <div className="text-sm font-semibold text-amber-600 dark:text-amber-400">
                            {keyMissing && secretMissing ? "API credentials not set" : keyMissing ? "SYNC_API_KEY not set" : "SYNC_HMAC_SECRET not set"}
                        </div>
                        <p className="text-xs text-muted-foreground mt-0.5">
                            The scraper cannot authenticate without these credentials.
                            Click <strong>Generate Both</strong> below to create them instantly — no server access needed.
                        </p>
                    </div>
                </div>
            )}

            {/* Enable / disable */}
            <Section title="Endpoint Control" icon={ToggleRight}>
                <div className="flex items-center justify-between py-2">
                    <div>
                        <div className="text-sm font-medium">Scraper endpoint enabled</div>
                        <div className="text-xs text-muted-foreground mt-0.5">
                            When disabled, all POST /sync/case and GET /sync/cnrs requests return 503.
                        </div>
                    </div>
                    <button
                        data-testid="toggle-scraper-enabled"
                        onClick={() => setForm(f => ({ ...f, enabled: !f.enabled }))}
                        className={`flex items-center gap-2 px-3 py-1.5 rounded-lg border text-sm font-medium transition-colors ${
                            form.enabled
                                ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-600 dark:text-emerald-400"
                                : "border-border bg-muted text-muted-foreground"
                        }`}
                    >
                        {form.enabled
                            ? <><ToggleRight className="w-4 h-4" /> Enabled</>
                            : <><ToggleLeft  className="w-4 h-4" /> Disabled</>
                        }
                    </button>
                </div>
            </Section>

            {/* Batch size */}
            <Section title="Batch Configuration" icon={Hash}>
                <div className="space-y-3">
                    <div>
                        <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide block mb-1.5">
                            Max CNRs per batch
                        </label>
                        <div className="flex items-center gap-3">
                            <input
                                data-testid="input-max-batch-size"
                                type="number"
                                min={1}
                                max={500}
                                value={form.max_batch_size ?? 50}
                                onChange={e => setForm(f => ({ ...f, max_batch_size: e.target.value }))}
                                className="w-32 px-3 py-2 rounded-lg border border-input bg-background text-sm text-center tabular-nums focus:outline-none focus:ring-2 focus:ring-ring"
                            />
                            <span className="text-xs text-muted-foreground">Maximum 500 per request · Scraper loops until <code className="font-mono bg-muted px-1 rounded">has_more=false</code></span>
                        </div>
                    </div>
                    <div>
                        <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide block mb-1.5">
                            Description
                        </label>
                        <input
                            data-testid="input-scraper-description"
                            type="text"
                            value={form.description ?? ""}
                            onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                            className="w-full px-3 py-2 rounded-lg border border-input bg-background text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                            placeholder="Short description for this scraper integration"
                        />
                    </div>
                </div>
            </Section>

            {/* API Credentials */}
            <Section title="API Credentials" icon={Zap}>
                <div className="space-y-4 text-sm">
                    <CredentialRow
                        label="SYNC_API_KEY"
                        hint="Used for Bearer auth on all sync endpoints"
                        value={cfg?.api_key_full}
                        masked={cfg?.api_key_masked}
                        source={cfg?.api_key_source}
                        show={showKey}
                        isSet={cfg?.api_key_set}
                        onToggle={() => setShowKey(v => !v)}
                        onCopy={() => copyToClipboard(cfg?.api_key_full, "API key")}
                        onRotate={rotateKey}
                        onSaveCustom={saveCustomKey}
                        rotating={rotatingKey}
                        testId="cred-api-key"
                    />
                    <CredentialRow
                        label="SYNC_HMAC_SECRET"
                        hint="Used for X-Signature-256 HMAC on POST /sync/case"
                        value={cfg?.hmac_secret_full}
                        masked={cfg?.hmac_secret_masked}
                        source={cfg?.hmac_secret_source}
                        show={showSecret}
                        isSet={cfg?.hmac_secret_set}
                        onToggle={() => setShowSecret(v => !v)}
                        onCopy={() => copyToClipboard(cfg?.hmac_secret_full, "HMAC secret")}
                        onRotate={rotateSecret}
                        onSaveCustom={saveCustomSecret}
                        rotating={rotatingSecret}
                        testId="cred-hmac-secret"
                    />

                    <div className="flex items-center gap-3 pt-1 flex-wrap">
                        <button
                            data-testid="btn-generate-both"
                            onClick={generateBoth}
                            disabled={rotatingKey || rotatingSecret}
                            className="flex items-center gap-2 px-4 py-2 rounded-lg bg-primary text-primary-foreground text-xs font-semibold hover:bg-primary/90 disabled:opacity-50 transition-colors"
                        >
                            {(rotatingKey || rotatingSecret) ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <KeyRound className="w-3.5 h-3.5" />}
                            Generate Both
                        </button>
                        <p className="text-xs text-muted-foreground flex-1">
                            Credentials saved to database — no server restart needed.
                            Share the generated keys with your scraper team.
                        </p>
                    </div>
                </div>
            </Section>

            {/* Save */}
            <button
                data-testid="btn-save-scraper-config"
                onClick={save}
                disabled={saving}
                className="flex items-center gap-2 px-5 py-2.5 rounded-lg bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90 disabled:opacity-50 transition-colors"
            >
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                Save configuration
            </button>
        </div>
    );
}

function CredentialRow({ label, hint, value, masked, source, show, isSet, onToggle, onCopy, onRotate, onSaveCustom, rotating, testId }) {
    const [editing, setEditing]   = useState(false);
    const [draft, setDraft]       = useState("");
    const [saving, setSaving]     = useState(false);

    const handleSave = async () => {
        const trimmed = draft.trim();
        if (!trimmed) return;
        setSaving(true);
        try {
            await onSaveCustom(trimmed);
            setEditing(false);
            setDraft("");
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className={`rounded-lg border p-3 space-y-2 transition-colors ${isSet ? "border-border bg-muted/40" : "border-amber-500/40 bg-amber-500/5"}`}
            data-testid={`cred-row-${testId}`}>
            <div className="flex items-center justify-between gap-2 flex-wrap">
                <div className="flex items-center gap-2">
                    <span className="text-xs font-mono font-semibold">{label}</span>
                    {isSet
                        ? <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${source === "db" ? "bg-emerald-500/15 text-emerald-600 dark:text-emerald-400" : "bg-sky-500/15 text-sky-600 dark:text-sky-400"}`}>
                            {source === "db" ? "DB" : "ENV"}
                          </span>
                        : <span className="text-[10px] px-1.5 py-0.5 rounded font-medium bg-amber-500/15 text-amber-600 dark:text-amber-400">NOT SET</span>
                    }
                </div>
                <div className="flex items-center gap-1.5">
                    {isSet && !editing && (
                        <>
                            <button data-testid={`btn-toggle-${testId}`} onClick={onToggle}
                                className="p-1.5 rounded hover:bg-muted transition-colors text-muted-foreground hover:text-foreground"
                                title={show ? "Hide" : "Reveal"}>
                                {show ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                            </button>
                            <button data-testid={`btn-copy-${testId}`} onClick={onCopy}
                                className="p-1.5 rounded hover:bg-muted transition-colors text-muted-foreground hover:text-foreground"
                                title="Copy to clipboard">
                                <Copy className="w-3.5 h-3.5" />
                            </button>
                        </>
                    )}
                    {!editing && (
                        <>
                            <button data-testid={`btn-paste-${testId}`}
                                onClick={() => { setDraft(""); setEditing(true); }}
                                className="flex items-center gap-1 px-2.5 py-1 rounded text-[11px] font-medium border border-border text-muted-foreground hover:border-primary/40 hover:text-primary disabled:opacity-40 transition-colors whitespace-nowrap">
                                <ClipboardList className="w-3 h-3" />
                                Paste
                            </button>
                            <button data-testid={`btn-rotate-${testId}`} onClick={onRotate}
                                disabled={rotating}
                                className="flex items-center gap-1 px-2.5 py-1 rounded text-[11px] font-medium border border-primary/40 text-primary hover:bg-primary/10 disabled:opacity-40 transition-colors whitespace-nowrap">
                                {rotating ? <Loader2 className="w-3 h-3 animate-spin" /> : <RotateCcw className="w-3 h-3" />}
                                {isSet ? "Rotate" : "Generate"}
                            </button>
                        </>
                    )}
                </div>
            </div>

            {/* Current value display */}
            {!editing && (
                <div className="font-mono text-xs break-all text-foreground/80 min-h-[1.1rem]">
                    {isSet ? (show ? value : masked) : <span className="text-amber-500 italic">Not configured — click Generate or Paste</span>}
                </div>
            )}

            {/* Inline paste editor */}
            {editing && (
                <div className="space-y-2">
                    <input
                        autoFocus
                        data-testid={`input-paste-${testId}`}
                        type="text"
                        value={draft}
                        onChange={e => setDraft(e.target.value)}
                        onKeyDown={e => { if (e.key === "Enter") handleSave(); if (e.key === "Escape") setEditing(false); }}
                        placeholder={`Paste your ${label} here…`}
                        className="w-full font-mono text-xs px-3 py-2 rounded border border-primary/50 bg-background focus:outline-none focus:ring-1 focus:ring-primary"
                    />
                    <div className="flex items-center gap-2">
                        <button
                            data-testid={`btn-save-paste-${testId}`}
                            onClick={handleSave}
                            disabled={!draft.trim() || saving}
                            className="flex items-center gap-1.5 px-3 py-1.5 rounded text-[11px] font-medium bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-40 transition-colors">
                            {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />}
                            Save
                        </button>
                        <button onClick={() => setEditing(false)}
                            className="px-3 py-1.5 rounded text-[11px] text-muted-foreground hover:text-foreground border border-border hover:border-primary/30 transition-colors">
                            Cancel
                        </button>
                    </div>
                </div>
            )}

            <div className="text-xs text-muted-foreground">{hint}</div>
        </div>
    );
}

// ── Activity ─────────────────────────────────────────────────────────────────

function ActivityTab() {
    const [data, setData]       = useState(null);
    const [loading, setLoading] = useState(true);
    const [skip, setSkip]       = useState(0);
    const LIMIT = 25;

    const load = useCallback(async (s = 0) => {
        setLoading(true);
        try {
            const { data: res } = await api.get(`/admin/scraper/activity?skip=${s}&limit=${LIMIT}`);
            setData(res);
            setSkip(s);
        } catch (e) {
            toast.error(apiError(e));
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(0); }, [load]);

    return (
        <div className="space-y-4">
            <div className="flex items-center justify-between">
                <div className="text-sm text-muted-foreground">
                    {data ? `${data.total} cases synced via scraper` : "Loading…"}
                </div>
                <button
                    data-testid="btn-refresh-activity"
                    onClick={() => load(skip)}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-border text-xs hover:bg-muted transition-colors"
                >
                    <RefreshCw className="w-3 h-3" /> Refresh
                </button>
            </div>

            {loading ? (
                <LoadingSpinner />
            ) : !data?.activity?.length ? (
                <EmptyState message="No scraper-synced cases yet. Push a case via POST /api/v1/sync/case to see it here." />
            ) : (
                <>
                    {/* Mobile: card list */}
                    <div className="sm:hidden space-y-2">
                        {data.activity.map((row, i) => (
                            <div
                                key={row.cnr ?? i}
                                data-testid={`activity-row-${i}`}
                                className="rounded-lg border border-border bg-card p-3 space-y-1.5"
                            >
                                <div className="flex items-start justify-between gap-2">
                                    <span className="font-mono text-xs font-semibold break-all leading-snug">{row.cnr ?? "—"}</span>
                                    {row.status && (
                                        <span className="shrink-0 px-1.5 py-0.5 rounded text-[10px] bg-muted text-muted-foreground">
                                            {row.status}
                                        </span>
                                    )}
                                </div>
                                {row.title && (
                                    <div className="text-xs text-foreground/70 leading-snug">{row.title}</div>
                                )}
                                <div className="text-[11px] text-muted-foreground">
                                    {row.external_synced_at ? fmtDateTime(row.external_synced_at) : "—"}
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* Desktop: table */}
                    <div className="hidden sm:block rounded-xl border border-border overflow-hidden">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="border-b border-border bg-muted/50">
                                    <th className="text-left px-4 py-2.5 text-xs uppercase tracking-widest text-muted-foreground font-medium w-10">#</th>
                                    <th className="text-left px-4 py-2.5 text-xs uppercase tracking-widest text-muted-foreground font-medium">CNR</th>
                                    <th className="text-left px-4 py-2.5 text-xs uppercase tracking-widest text-muted-foreground font-medium">Title / Status</th>
                                    <th className="text-left px-4 py-2.5 text-xs uppercase tracking-widest text-muted-foreground font-medium whitespace-nowrap">Synced at</th>
                                </tr>
                            </thead>
                            <tbody>
                                {data.activity.map((row, i) => (
                                    <tr
                                        key={row.cnr ?? i}
                                        data-testid={`activity-row-${i}`}
                                        className="border-b border-border last:border-0 hover:bg-muted/30 transition-colors"
                                    >
                                        <td className="px-4 py-3 text-xs text-muted-foreground tabular-nums">{skip + i + 1}</td>
                                        <td className="px-4 py-3 font-mono text-xs font-medium whitespace-nowrap">{row.cnr ?? "—"}</td>
                                        <td className="px-4 py-3 max-w-[220px]">
                                            <div className="text-xs text-foreground/80 truncate">{row.title || "—"}</div>
                                            {row.status && (
                                                <span className="mt-0.5 inline-block px-1.5 py-0.5 rounded text-[10px] bg-muted text-muted-foreground">
                                                    {row.status}
                                                </span>
                                            )}
                                        </td>
                                        <td className="px-4 py-3 text-xs text-muted-foreground whitespace-nowrap">
                                            {row.external_synced_at ? fmtDateTime(row.external_synced_at) : "—"}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    {/* Pagination */}
                    <div className="flex items-center justify-between text-xs text-muted-foreground">
                        <span>Showing {skip + 1}–{skip + data.count} of {data.total}</span>
                        <div className="flex gap-2">
                            <button
                                data-testid="btn-activity-prev"
                                onClick={() => load(Math.max(0, skip - LIMIT))}
                                disabled={skip === 0}
                                className="px-3 py-1.5 rounded border border-border hover:bg-muted disabled:opacity-40 transition-colors"
                            >
                                Previous
                            </button>
                            <button
                                data-testid="btn-activity-next"
                                onClick={() => load(skip + LIMIT)}
                                disabled={!data.has_more}
                                className="px-3 py-1.5 rounded border border-border hover:bg-muted disabled:opacity-40 transition-colors"
                            >
                                Next
                            </button>
                        </div>
                    </div>
                </>
            )}
        </div>
    );
}

// ── Shared helpers ────────────────────────────────────────────────────────────

function Section({ title, icon: Icon, children }) {
    return (
        <div className="rounded-xl border border-border bg-card p-5 space-y-4">
            <div className="flex items-center gap-2 text-sm font-semibold">
                <Icon className="w-4 h-4 text-primary" strokeWidth={1.5} />
                {title}
            </div>
            {children}
        </div>
    );
}

function LoadingSpinner() {
    return (
        <div className="flex items-center justify-center py-16 text-muted-foreground">
            <Loader2 className="w-5 h-5 animate-spin mr-2" />
            <span className="text-sm">Loading…</span>
        </div>
    );
}

function EmptyState({ message }) {
    return (
        <div className="flex flex-col items-center justify-center py-16 text-center text-muted-foreground gap-3">
            <AlertTriangle className="w-8 h-8 opacity-30" />
            <p className="text-sm max-w-sm">{message}</p>
        </div>
    );
}
